// The `/library` MODULE LANDING route. The seven LIB consoles each own their
// own subtree (`library/catalogue`, `library/patrons`, …) hung off the portal
// root; none of them claimed the bare `/library` path, so the sidebar's
// "Library" area pointed at a route that rendered nothing.
//
// Deliberately a SIBLING of the console subtrees rather than their parent:
// making it a parent would nest every console under one more layout and
// re-order the whole tree for a landing page. This adds one route and touches
// nothing else.
import { lazy } from "solid-js";
import { createRoute } from "@tanstack/solid-router";
import { ConsoleLayout } from "@erp/ui/console-layout";
import type { AnyRoute } from "@tanstack/solid-router";

// Lazy like every other console screen; the route below is eager only because
// it carries the gate.

const LibraryOverviewLazy = lazy(() =>
  import("../../screens/admin/Overview.js").then((m) => ({ default: m.LibraryOverview })),
);

export function makeLibOverviewRoute<TParent extends AnyRoute>(rootRoute: TParent) {
  // lib:catalog.read is the widest LIB grant — anyone who can open ANY console
  // holds it or more, so it gates the hub without hiding it from a role that
  // has business here. The hub re-filters its own console tiles per grant.
  const overviewRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "library",
    component: () => (
      <ConsoleLayout
        perm="lib:catalog.read"
        deniedMessage="This account cannot open the library. Ask an administrator for catalogue access."
        skeletonId="library-overview-skeleton"
        deniedId="library-overview-denied"
        unreachableId="library-overview-unreachable"
      >
        <LibraryOverviewLazy />
      </ConsoleLayout>
    ),
  });
  // Phone pairing had a route here until 2026-10-02. It is a header control
  // now (the owner): one dialog did not need a screen, and the desk had been
  // opening the same dialog from a button since it was built. The PANEL stays
  // in the module — BulkAddBooks embeds it, and the staff desk keeps its own
  // /library/desk/devices screen, which is a real page with a device roster.

  return [overviewRoute];
}
